import type { ReactNode } from 'react'

import { useCustomerHub } from '../contexts/CustomerHubContext'
import { cn } from '../lib/utils'

import Skeleton from './Skeleton'

/**
 * Nav placeholder width per tab (RP-5936 handoff prototype), proportional to
 * each real label so the swap to the rendered nav reads as the same row.
 */
const NAV_BAR_WIDTH_BY_TAB: Record<string, number> = {
  overview: 92,
  orders: 68,
  sizePassport: 124,
  wardrobe: 96,
  settings: 78,
}

/** Width for a tab the caller knows about but this map does not. */
const DEFAULT_NAV_BAR_WIDTH = 92

/**
 * Boot placeholder for the Hub shell — the customer name and the tab nav
 * (RP-5936). It replaces the centred 2px `Loader` that used to hold the whole
 * page while the customer record was fetched, so the Hub never shows a blank
 * page or a lone spinner on first load.
 *
 * The scaffold below (page wrapper, header rail, nav, main) mirrors
 * `CustomerHubContent` exactly, so revealing the real shell causes no layout
 * shift. The section skeletons take over inside `main` once the sections
 * mount — the shell settles first, then each section on its own timing.
 *
 * `tabIds` must be the same role-aware list the loaded nav renders on first
 * paint, so the placeholder never promises tabs that will not appear. The
 * flag-gated tabs (Size Passport, Wardrobe) are deliberately not assumed: they
 * join the real nav only once GrowthBook resolves, which happens after this
 * placeholder is gone.
 */
export default function HubShellSkeleton({
  className,
  tabIds,
  children,
}: {
  className?: string
  tabIds: readonly string[]
  /** The opening tab's placeholders, painted in the same frame as the bars. */
  children?: ReactNode
}) {
  const { context } = useCustomerHub()

  // Mirrors CustomerHubContent so the loading and loaded frames match.
  const isAuthRedirect =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('authRedirect') === 'true'
  const reserveFixedFooterInset =
    !isAuthRedirect &&
    (context === 'active-customers-list' || context === 'predict-spring')

  return (
    <div
      className={cn(
        'bg-background min-h-full lg:flex lg:gap-20 lg:px-80 lg:pt-64 lg:pb-120',
        'max-lg:flex max-lg:flex-col max-lg:overflow-hidden',
        reserveFixedFooterInset
          ? 'max-lg:min-h-0 max-lg:flex-1'
          : 'max-lg:min-h-dvh max-lg:max-h-dvh',
        className,
      )}
      aria-busy="true"
      data-testid="hub-shell-skeleton"
    >
      <header className="flex shrink-0 flex-col max-lg:pt-40 lg:w-5/12 xl:w-1/3">
        <div className="flex flex-col gap-10 lg:gap-40">
          {/* Customer name: 260px wide, filling the h1's line box on each
              breakpoint (leading-2xl 32px / leading-3xl 36px). */}
          <div className="flex items-start gap-10 max-lg:px-16">
            <Skeleton w={260} className="max-w-full h-32 lg:h-36" />
          </div>

          {/* Tab nav: a 20px bar centred in each tab's 24px line box
              (leading-base), inside the nav's own container. */}
          <nav className="flex flex-nowrap max-lg:gap-24 max-lg:px-16 max-lg:border-b max-lg:border-border lg:flex-col lg:items-start lg:gap-16 overflow-x-auto scrollbar-hide">
            {tabIds.map((tabId) => (
              <div key={tabId} className="shrink-0 max-lg:py-12">
                <Skeleton
                  w={NAV_BAR_WIDTH_BY_TAB[tabId] ?? DEFAULT_NAV_BAR_WIDTH}
                  h={20}
                  className="my-2"
                />
              </div>
            ))}
          </nav>
        </div>
      </header>

      {/* The opening tab's placeholders sit here, so the page reads as one
          skeleton rather than a header that loads before its content. */}
      <main
        className={cn(
          'relative lg:w-7/12 xl:w-2/3',
          'max-lg:min-h-0 max-lg:flex-1 max-lg:overflow-y-auto max-lg:overscroll-y-contain',
        )}
      >
        {children}
      </main>
    </div>
  )
}
